import { useEffect } from 'react';
import { useFormik, FormikProvider, type FormikProps } from 'formik';
import { Box, Flex } from '@chakra-ui/react';
import { Checkbox } from '@kula-ai/components';
import { useQuery } from '@tanstack/react-query';
import { http } from '@/lib/ats-https';
import { ScheduleRejectionEmail } from '@/components/shared/rejection-email/ScheduleRejectionEmail';
import { AutoRejectEmailComposer } from './AutoRejectEmailComposer';
import type { AutoRejectTemplate } from './autoRejectEmailValue';
import {
  AUTO_REJECT_EMAIL_DEFAULTS,
  type AutoRejectEmailFormValues,
} from '../autoRejectEmail.types';

type FormValues = AutoRejectEmailFormValues;

interface AutoRejectEmailSectionProps {
  /**
   * The parent reads `formRef.current.values` at save time to build the
   * payload. Mirrors the fraud-detector `formRef` pattern so the drawer's
   * local-state save flow can stay non-Formik.
   */
  formRef: React.MutableRefObject<FormikProps<FormValues> | null>;
  initialValues?: FormValues;
  /** Notifies the parent when the email form's dirty state changes, so the
   * drawer can enable/disable its save / Test Scoring agent buttons. */
  onDirtyChange?: (_dirty: boolean) => void;
}

/**
 * Self-contained Formik "island" for configuring the AI-scoring auto-reject
 * rejection email: a "Send rejection email" checkbox that reveals the kula-ai
 * `Email` composer (template picker, sender, body) and the scheduling controls.
 * Used inside the auto-reject section of the Scoring agent drawer/edit view.
 */
export const AutoRejectEmailSection = ({
  formRef,
  initialValues,
  onDirtyChange,
}: AutoRejectEmailSectionProps) => {
  const formik = useFormik<FormValues>({
    initialValues: initialValues ?? AUTO_REJECT_EMAIL_DEFAULTS,
    enableReinitialize: true,
    onSubmit: () => {},
  });

  // Expose the live formik instance to the parent for save-time reads.
  useEffect(() => {
    formRef.current = formik;
  }, [formRef, formik]);

  // Report dirty changes so the drawer can toggle its save / test buttons.
  const { dirty } = formik;
  useEffect(() => {
    onDirtyChange?.(dirty);
  }, [dirty, onDirtyChange]);

  const { data: templateOptions = [] } = useQuery<AutoRejectTemplate[]>({
    queryKey: ['aiScoringAutoReject', 'emailTemplates'],
    queryFn: async () => {
      const res = (await http.get(
        '/ats_comm_templates?filter_by[category][]=candidate_application_rejected&items=200&filter_by[name]='
      )) as { data: EmailTemplate[] };
      return res?.data ?? [];
    },
    staleTime: 0,
  });

  const { values, setFieldValue, setValues } = formik;
  const { sendRejectEmail } = values;

  return (
    // FormikProvider shares this island's useFormik() instance via context so
    // the reused email fields (which use Formik's <Field>/useField internally)
    // resolve getFieldProps — FormBuilder does the same via its <Formik>.
    <FormikProvider value={formik}>
      <Box mt="16px">
        <Flex gap="8px" alignItems="center">
          <Checkbox
            size="sm"
            label="Send rejection email"
            labelClassName="!text-sm !font-[550] !text-secondary-900"
            checked={sendRejectEmail}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              const checked = e.target.checked;
              if (!checked) {
                setValues({
                  ...values,
                  sendRejectEmail: false,
                  selectedTemplate: null,
                  subject: '',
                  body: '',
                });
              } else {
                setFieldValue('sendRejectEmail', true);
              }
            }}
          />
        </Flex>

        {sendRejectEmail && (
          <Box mt="12px">
            <AutoRejectEmailComposer
              formik={formik}
              templates={templateOptions}
            />
            <Box mt="12px">
              <ScheduleRejectionEmail formik={formik as any} />
            </Box>
          </Box>
        )}
      </Box>
    </FormikProvider>
  );
};
